import {
  X,
  Maximize,
  Minimize,
  PhoneOff,
  MicOff,
  Mic,
  Video,
  VideoOff,
} from "lucide-react";
import { AvailablePatient } from "../../types/appointment";
import { useEffect, useRef, useState } from "react";
import {
  LocalUser,
  RemoteUser,
  useIsConnected,
  useJoin,
  useLocalCameraTrack,
  useLocalMicrophoneTrack,
  usePublish,
  useRemoteUsers,
  useRTCClient,
} from "agora-rtc-react";
import { VideoSdkService } from "../../services/api/video.service";
import { motion, AnimatePresence, PanInfo } from "framer-motion";

interface VideoCallModalProps {
  isOpen: boolean;
  onClose: () => void;
  doctorName: string;
  patient: AvailablePatient;
  uid: string;
  channelName: string;
  doctorProfile: string;
}

export function VideoCallModal({
  isOpen,
  onClose,
  uid,
  channelName,
  doctorName,
  doctorProfile,
}: VideoCallModalProps) {
  const [calling, setCalling] = useState(false);
  const isConnected = useIsConnected();
  const [token, setToken] = useState("");
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragConstraintsRef = useRef<HTMLDivElement>(null);
  const client = useRTCClient();
  const [dragPosition, setDragPosition] = useState(0);
  const [width, setWidth] = useState(30);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);

  // Snap points in percentages
  const snapPoints = [30, 80];

  // Function to find nearest snap point
  const findNearestSnapPoint = (currentWidth: number) => {
    console.log(currentWidth);
    if (currentWidth > 60) {
      setIsFullScreen(true);
    }
    return snapPoints.reduce((prev, curr) => {
      return Math.abs(curr - currentWidth) < Math.abs(prev - currentWidth)
        ? curr
        : prev;
    });
  };

  const APP_ID = process.env.REACT_APP_VIDEO_APP_ID;

  // Add connection retry state
  const [connectionAttempts, setConnectionAttempts] = useState(0);
  const MAX_RETRY_ATTEMPTS = 3;

  // Add state to track if we should publish tracks
  const [shouldPublish, setShouldPublish] = useState(true);

  // Initialize camera and mic state to true when modal opens
  useEffect(() => {
    if (isOpen) {
      setCamera(true);
      setMic(true);
      setShouldPublish(true);
    } else {
      handleDisconnect();
    }
  }, [isOpen]);

  // Modify the token fetching logic
  useEffect(() => {
    let mounted = true;

    const getVideoToken = async () => {
      try {
        const response = await VideoSdkService.getVideoToken({
          uid: 0,
          channelName: channelName,
        });

        if (mounted && response) {
          setToken(response.token);
          // Add delay before setting calling state
          setTimeout(() => {
            if (mounted) {
              setCalling(true);
            }
          }, 1000);
        }
      } catch (error) {
        console.error("Failed to get video token:", error);
        if (connectionAttempts < MAX_RETRY_ATTEMPTS) {
          setConnectionAttempts((prev) => prev + 1);
          // Retry after 2 seconds
          setTimeout(getVideoToken, 2000);
        }
      }
    };

    if (isOpen) {
      getVideoToken();
    }

    return () => {
      mounted = false;
    };
  }, [isOpen, channelName, connectionAttempts]);

  // Join the channel when calling and token are ready
  useJoin(
    {
      appid: APP_ID,
      channel: channelName,
      token: token,
    },
    calling && !!token
  );

  const [micOn, setMic] = useState(true);
  const [cameraOn, setCamera] = useState(true);

  // Create tracks when modal is open and not disconnecting
  const { localMicrophoneTrack } = useLocalMicrophoneTrack(
    isOpen && !isDisconnecting
  );
  const { localCameraTrack } = useLocalCameraTrack(isOpen && !isDisconnecting);

  // Fixed: Only publish tracks when they should be published AND when mic/camera is on
  usePublish(
    isOpen && shouldPublish && !isDisconnecting
      ? [
          micOn ? localMicrophoneTrack : null,
          cameraOn ? localCameraTrack : null,
        ].filter(Boolean)
      : []
  );

  const remoteUsers = useRemoteUsers();

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      handleDisconnect();
    };
  }, []);

  // Fixed: Handle mic toggle with proper state updates
  const handleMicToggle = async () => {
    try {
      const newMicState = !micOn;
      setMic(newMicState);

      if (localMicrophoneTrack) {
        await localMicrophoneTrack.setEnabled(newMicState);
      }
    } catch (error) {
      console.error("Error toggling microphone:", error);
      setMic(micOn); // Revert state on error
    }
  };

  // Fixed: Handle camera toggle with proper state updates
  const handleCameraToggle = async () => {
    try {
      const newCameraState = !cameraOn;
      setCamera(newCameraState);

      if (localCameraTrack) {
        await localCameraTrack.setEnabled(newCameraState);
      }
    } catch (error) {
      console.error("Error toggling camera:", error);
      setCamera(cameraOn); // Revert state on error
    }
  };

  // Improved disconnect handler
  const handleDisconnect = async () => {
    setIsDisconnecting(true);
    setShouldPublish(false);

    try {
      // First disable tracks
      if (localCameraTrack) {
        await localCameraTrack.setEnabled(false);
        await localCameraTrack.stop();
        await localCameraTrack.close();
      }

      if (localMicrophoneTrack) {
        await localMicrophoneTrack.setEnabled(false);
        await localMicrophoneTrack.stop();
        await localMicrophoneTrack.close();
      }

      // Clear video reference
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }

      // Leave the channel
      if (client && client.connectionState !== "DISCONNECTED") {
        await client.leave();
      }

      // Reset states
      setCalling(false);
      setCamera(false);
      setMic(false);
      setIsFullScreen(false);
    } catch (error) {
      console.error("Error during disconnection:", error);
    } finally {
      setIsDisconnecting(false);
      onClose();
    }
  };

  // Add effect to handle client state changes
  useEffect(() => {
    if (!client) return;

    const handleConnectionStateChange = (state: string) => {
      if (state === "DISCONNECTED") {
        handleDisconnect();
      }
    };

    client.on("connection-state-change", handleConnectionStateChange);

    return () => {
      client.off("connection-state-change", handleConnectionStateChange);
    };
  }, [client]);

  // Calculate width based on drag position
  const getWidth = () => {
    if (isFullScreen) {
      return "100%";
    }

    if (dragPosition !== 0) {
      // Convert drag position to width percentage
      const calculatedWidth = 80 - dragPosition / 5;
      return `${Math.max(20, Math.min(80, calculatedWidth))}%`;
    }
    return `30%`;
  };

  // Handle drag end with snap points
  const handleDragEnd = (info: PanInfo) => {
    setIsDragging(false);
    const currentWidth = 80 - info.offset.x / 5;
    const snappedWidth = findNearestSnapPoint(currentWidth);
    setWidth(snappedWidth);
    setDragPosition(0);
  };

  // Toggle fullscreen mode
  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen);
  };

  // Handlers for ESC key to exit fullscreen
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isFullScreen) {
        setIsFullScreen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isFullScreen]);

  // Add effect to properly enable/disable tracks based on state
  useEffect(() => {
    if (!localMicrophoneTrack) return;

    const updateMicState = async () => {
      try {
        await localMicrophoneTrack.setEnabled(micOn);
      } catch (error) {
        console.error("Error updating mic state:", error);
      }
    };

    updateMicState();
  }, [micOn, localMicrophoneTrack]);

  useEffect(() => {
    if (!localCameraTrack) return;

    const updateCameraState = async () => {
      try {
        await localCameraTrack.setEnabled(cameraOn);
      } catch (error) {
        console.error("Error updating camera state:", error);
      }
    };

    updateCameraState();
  }, [cameraOn, localCameraTrack]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        ref={dragConstraintsRef}
        className="fixed inset-0 z-50 overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <div
          className="absolute inset-0"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.75)",
          }}
        />

        <motion.div
          drag={isFullScreen ? false : "x"}
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.2}
          onDragStart={() => !isFullScreen && setIsDragging(true)}
          onDrag={(event, info) => {
            if (!isFullScreen) setDragPosition(info.offset.x);
          }}
          onDragEnd={(_, info) => !isFullScreen && handleDragEnd(info)}
          initial={{ x: "100%", width: "80%" }}
          animate={{
            x: 0,
            width: getWidth(),
            right: 0, // Always keep the modal at the right side
            position: "absolute",
            insetY: 0,
          }}
          exit={{ x: "100%" }}
          transition={{
            type: "spring",
            damping: 25,
            stiffness: 180,
          }}
          className="absolute inset-y-0 right-0 bg-[#1a1a1a] shadow-xl"
          style={{
            cursor: isDragging ? "grabbing" : "grab",
            zIndex: isFullScreen ? 100 : 50,
          }}
        >
          {/* Drag Handle - Only visible when not in fullscreen */}
          {!isFullScreen && (
            <div
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 
                         w-6 h-24 bg-gray-200 rounded-l-full flex items-center 
                         justify-center cursor-grab active:cursor-grabbing"
            >
              <div className="w-1 h-12 bg-gray-400 rounded-full" />
            </div>
          )}

          {/* Header */}
          <div className="p-4 border-b border-gray-700 flex justify-between items-center">
            <h3 className="text-lg font-semibold text-white">
              {isConnected ? "Ongoing Call" : "Connecting..."}
            </h3>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-300">
                {new Date().toLocaleTimeString()}
              </span>
              <button
                onClick={toggleFullScreen}
                className="text-gray-400 hover:text-gray-200 p-1"
              >
                {isFullScreen ? (
                  <Minimize className="w-5 h-5" />
                ) : (
                  <Maximize className="w-5 h-5" />
                )}
              </button>
              <button
                onClick={handleDisconnect}
                className="text-gray-400 hover:text-gray-200"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="h-[calc(100vh-64px)] relative">
            {isConnected ? (
              <div
                className={`flex h-full ${
                  isFullScreen
                    ? "flex-row items-center justify-center gap-2"
                    : "flex-col"
                }`}
              >
                {/* Main Video (Remote User) */}
                <div
                  className={`${
                    isFullScreen ? "h-full w-1/2" : "h-[50%]"
                  } p-4 pointer-events-none relative`}
                >
                  <div className="relative h-full rounded-lg overflow-hidden bg-gray-800 flex items-center justify-center">
                    {remoteUsers.length > 0 ? (
                      <RemoteUser
                        user={remoteUsers[0]}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                        className="pointer-events-none"
                      >
                        <div className="absolute top-4 left-4 z-10 flex items-center gap-2">
                          <div className="w-12 h-12 flex items-center justify-center overflow-hidden rounded-full">
                            <img
                              src={doctorProfile}
                              alt={`${doctorName}-Profile`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <span className="text-white font-medium bg-black/50 px-3 py-1 rounded-full">
                            {doctorName || `User ${remoteUsers[0].uid}`}
                          </span>
                        </div>
                      </RemoteUser>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white">
                        <span className="text-lg">
                          Waiting for others to join...
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Local Video */}
                <div
                  className={`${
                    isFullScreen ? "h-full w-1/2" : "h-[50%]"
                  } p-4 pointer-events-none relative`}
                >
                  {!isFullScreen && (
                    <div className="absolute top-0 right-0 m-6 z-10">
                      <span className="text-white text-xs font-medium bg-black/50 px-2 py-1 rounded-full">
                        You
                      </span>
                    </div>
                  )}

                  <div className="h-full rounded-lg overflow-hidden bg-gray-800 flex items-center justify-center">
                    <LocalUser
                      audioTrack={localMicrophoneTrack}
                      videoTrack={localCameraTrack}
                      cameraOn={cameraOn}
                      micOn={micOn}
                      playAudio={false}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                      className="pointer-events-none"
                    >
                      {isFullScreen && (
                        <div className="absolute top-4 left-4 z-10">
                          <span className="text-white font-medium bg-black/50 px-3 py-1 rounded-full">
                            You
                          </span>
                        </div>
                      )}
                    </LocalUser>
                  </div>
                </div>

                {/* Controls */}
                <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex gap-3 bg-black/60 backdrop-blur-sm p-3 rounded-full shadow-lg z-50 pointer-events-auto">
                  <button
                    className={`p-3 rounded-full transition-colors ${
                      micOn
                        ? "bg-gray-700 hover:bg-gray-600"
                        : "bg-red-500 hover:bg-red-600"
                    }`}
                    onClick={handleMicToggle}
                  >
                    {micOn ? (
                      <Mic className="text-white w-5 h-5" />
                    ) : (
                      <MicOff className="text-white w-5 h-5" />
                    )}
                  </button>
                  <button
                    className={`p-3 rounded-full transition-colors ${
                      cameraOn
                        ? "bg-gray-700 hover:bg-gray-600"
                        : "bg-red-500 hover:bg-red-600"
                    }`}
                    onClick={handleCameraToggle}
                  >
                    {cameraOn ? (
                      <Video className="text-white w-5 h-5" />
                    ) : (
                      <VideoOff className="text-white w-5 h-5" />
                    )}
                  </button>
                  <button
                    className="p-3 rounded-full bg-gray-700 hover:bg-gray-600 transition-colors"
                    onClick={toggleFullScreen}
                  >
                    {isFullScreen ? (
                      <Minimize className="text-white w-5 h-5" />
                    ) : (
                      <Maximize className="text-white w-5 h-5" />
                    )}
                  </button>
                  <button
                    className="p-3 rounded-full bg-red-500 hover:bg-red-600 transition-colors"
                    onClick={handleDisconnect}
                  >
                    <PhoneOff className="text-white w-5 h-5" />
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-white">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4" />
                  <p className="text-lg">Connecting to call...</p>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
